import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PenLine, Sparkles, Copy, Check, Loader2, Megaphone, Mail, FileText, ShieldCheck, Send, Link2, FilePlus2, Search } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader } from "@/components/app/Loader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Badge } from "@/components/ui/badge";

const TYPES = [
  { v: "social", label: "Social", icon: Megaphone },
  { v: "press_release", label: "Press release", icon: FileText },
  { v: "email", label: "Volunteer email", icon: Mail },
];
const TONES = ["Optimistic & pragmatic", "Urgent & mobilizing", "Calm & factual", "Warm & personal", "Bold & contrast"];
const PROVIDERS = [
  { v: "auto", label: "Auto-route (recommended)" },
  { v: "anthropic", label: "Claude: long-form persuasion" },
  { v: "gemini", label: "Gemini: fast & grounded" },
  { v: "openai", label: "OpenAI: structured" },
];

export default function ContentStudio() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const feedId = sp.get("feed");
  const personaParam = sp.get("persona");
  const draftId = sp.get("draft");

  const [type, setType] = useState("social");
  const [tone, setTone] = useState(TONES[0]);
  const [personaId, setPersonaId] = useState(personaParam || "none");
  const [provider, setProvider] = useState("auto");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [usage, setUsage] = useState(null);
  const [filter, setFilter] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  const { data: personas } = useQuery({ queryKey: ["personas"], queryFn: () => api.get("/audience/personas").then((r) => r.data) });
  const { data: feedItem } = useQuery({
    queryKey: ["feedItem", feedId],
    queryFn: () => api.get("/listening/feed").then((r) => r.data.find((f) => f.id === feedId)),
    enabled: !!feedId,
  });
  const { data: drafts } = useQuery({ queryKey: ["drafts"], queryFn: () => api.get("/content/drafts").then((r) => r.data) });

  // Recall a saved draft straight from the URL, so every generation has a permalink.
  const { data: openedDraft, isFetching: openingDraft, isError: openFailed } = useQuery({
    queryKey: ["draft", draftId],
    queryFn: () => api.get(`/content/drafts/${draftId}`).then((r) => r.data),
    enabled: !!draftId,
    retry: false,
  });

  useEffect(() => {
    if (openedDraft) {
      setDraft(openedDraft);
      setUsage(null);
      setType(openedDraft.type || "social");
      if (openedDraft.prompt) setPrompt(openedDraft.prompt);
    }
  }, [openedDraft]);

  useEffect(() => {
    if (openFailed) toast.error("That draft no longer exists");
  }, [openFailed]);

  const openDraft = (id) => {
    const next = new URLSearchParams(sp);
    next.set("draft", id);
    setSp(next);
  };

  const newDraft = () => {
    const next = new URLSearchParams(sp);
    next.delete("draft");
    setSp(next);
    setDraft(null);
    setUsage(null);
  };

  const draftLink = (id) => `${window.location.origin}/studio?draft=${id}`;

  const copyLink = (id) => {
    navigator.clipboard.writeText(draftLink(id));
    setLinkCopied(true);
    toast.success("Link copied. Anyone on the team can open this draft");
    setTimeout(() => setLinkCopied(false), 1800);
  };

  useEffect(() => {
    if (feedItem) setPrompt((p) => p || `Craft our response to: "${feedItem.title}"`);
  }, [feedItem]);

  const generate = async () => {
    if (!prompt.trim() && !feedId) return toast.error("Add a prompt or come from a feed signal");
    setLoading(true);
    setDraft(null);
    try {
      const body = {
        content_type: type,
        source: feedId ? "feed" : "prompt",
        feed_item_id: feedId || undefined,
        prompt,
        tone,
        persona_id: personaId === "none" ? undefined : personaId,
        provider: provider === "auto" ? undefined : provider,
      };
      const { data } = await api.post("/content/generate", body);
      setDraft(data.draft);
      setUsage(data.usage);
      const next = new URLSearchParams(sp);
      next.set("draft", data.draft.id);
      setSp(next, { replace: true });
      qc.invalidateQueries({ queryKey: ["drafts"] });
      if (data.partial) {
        toast.warning("Only one of the two releases came back. Generate again for a second angle.");
      } else {
        toast.success("Content drafted");
      }
    } catch (e) {
      toast.error(apiError(e, "Generation failed"));
    } finally {
      setLoading(false);
    }
  };

  const publish = async () => {
    try {
      await api.patch(`/content/drafts/${draft.id}`, { status: "published" });
      toast.success("Marked published (copy-out)");
      setDraft((d) => ({ ...d, status: "published" }));
      qc.invalidateQueries({ queryKey: ["drafts"] });
      qc.invalidateQueries({ queryKey: ["draft", draft.id] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const q = filter.trim().toLowerCase();
  const visibleDrafts = !q ? (drafts || []) : (drafts || []).filter((d) =>
    `${d.type} ${d.origin} ${d.status} ${d.prompt || ""} ${d.variants?.map((v) => v.body).join(" ") || ""}`
      .toLowerCase().includes(q));

  return (
    <div>
      <PageHeader
        title="Content Studio"
        subtitle="Generate on-message social posts, press releases, and volunteer emails, tailored to a persona and what's resonating locally. Disclaimer auto-applied."
        icon={PenLine}
        actions={
          draft && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => copyLink(draft.id)} data-testid="copy-draft-link-btn">
                {linkCopied ? <Check className="mr-1.5 h-3.5 w-3.5 text-pos-text" /> : <Link2 className="mr-1.5 h-3.5 w-3.5" />} Copy link
              </Button>
              <Button variant="outline" onClick={newDraft} data-testid="new-draft-btn">
                <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> New draft
              </Button>
            </div>
          )
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
        {/* Controls */}
        <div className="space-y-5 rounded-xl border border-border bg-white p-6 shadow-sm">
          {feedItem && (
            <div className="rounded-lg border border-brand-navy/15 bg-brand-navy/[0.04] p-3">
              <p className="text-[0.65rem] font-bold uppercase tracking-wider text-brand-navy">Responding to signal</p>
              <p className="mt-1 text-sm font-semibold text-brand-ink">{feedItem.title}</p>
              <Link to="/feed" className="mt-1 inline-block text-xs text-brand-blue hover:underline">change signal</Link>
            </div>
          )}

          <div>
            <Label className="mb-2 block">Content type</Label>
            <ToggleGroup type="single" value={type} onValueChange={(v) => v && setType(v)} className="grid grid-cols-3 gap-2">
              {TYPES.map((t) => (
                <ToggleGroupItem
                  key={t.v}
                  value={t.v}
                  data-testid={`content-type-${t.v}`}
                  className="flex h-auto flex-col gap-1 border border-border py-2.5 data-[state=on]:border-brand-red data-[state=on]:bg-brand-red/10 data-[state=on]:text-brand-red"
                >
                  <t.icon className="h-4 w-4" />
                  <span className="text-[0.7rem] font-semibold">{t.label}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          <div>
            <Label htmlFor="prompt" className="mb-2 block">Prompt</Label>
            <Textarea
              id="prompt"
              data-testid="content-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Announce our endorsement of the schools millage and invite volunteers to canvass Saturday."
              rows={4}
            />
          </div>

          <Field label="Tone">
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="tone-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>

          <Field label="Target persona">
            <Select value={personaId} onValueChange={setPersonaId}>
              <SelectTrigger data-testid="persona-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No persona</SelectItem>
                {personas?.map((p) => <SelectItem key={p.id} value={p.id}>{p.label} ({p.size})</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <Field label="AI provider">
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>

          <Button data-testid="generate-content-btn" onClick={generate} disabled={loading} className="h-11 w-full bg-brand-red text-white hover:bg-brand-redhover active:scale-[0.98]">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate variants</>}
          </Button>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {(loading || openingDraft) && <Loader label={loading ? "Drafting on-message variants…" : "Opening saved draft…"} />}
          {!loading && !openingDraft && !draft && (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-white/60 p-10 text-center">
              <Sparkles className="mb-3 h-8 w-8 text-brand-red" />
              <h3 className="font-heading text-lg font-bold text-brand-ink">Your variants will appear here</h3>
              <p className="mt-1 max-w-sm text-sm text-brand-mute">Pick a type, add a prompt, and generate ready-to-paste options.</p>
            </div>
          )}

          {draft && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-white px-4 py-3 shadow-sm">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge className="bg-brand-navy/10 text-brand-navy">{draft.model_used}</Badge>
                  {draft.persona_label && <Badge variant="outline">Persona: {draft.persona_label}</Badge>}
                  {usage && <span className="text-xs text-brand-mute">~${usage.cost_estimate} · {usage.input_tokens + usage.output_tokens} tok (est.)</span>}
                </div>
                <div className="flex items-center gap-2">
                  {draft.type === "email" && (
                    <Button asChild variant="outline" size="sm"><Link to="/outreach"><Send className="mr-1.5 h-3.5 w-3.5" /> Send</Link></Button>
                  )}
                  {hasRole("admin", "manager") && draft.status !== "published" && (
                    <Button data-testid="publish-draft-btn" size="sm" onClick={publish} className="bg-pos-text text-white hover:opacity-90">
                      <Check className="mr-1.5 h-3.5 w-3.5" /> Approve & publish
                    </Button>
                  )}
                  {draft.status === "published" && <Badge className="bg-pos-bg text-pos-text">Published</Badge>}
                </div>
              </div>

              {draft.variants.map((v, i) => <VariantCard key={i} variant={v} index={i} />)}

              <p className="flex items-center gap-1.5 px-1 text-xs text-brand-mute">
                <ShieldCheck className="h-3.5 w-3.5 text-brand-blue" /> Disclaimer auto-applied. Nothing is posted automatically. Copy out and publish manually.
              </p>
            </>
          )}

          {/* Library: every generation stays recallable */}
          {drafts?.length > 0 && (
            <div className="rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="draft-library">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-heading text-base font-bold text-brand-ink">
                  Draft library <span className="text-sm font-normal text-brand-mute">({drafts.length})</span>
                </h3>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-brand-mute" />
                  <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter drafts…"
                    className="h-9 w-[210px] pl-8 text-sm" data-testid="draft-filter-input" />
                </div>
              </div>
              <p className="mt-1 text-xs text-brand-mute">Click any draft to reopen its variants. The URL becomes a shareable link.</p>
              <div className="mt-3 max-h-[420px] divide-y divide-border overflow-y-auto">
                {visibleDrafts.length === 0 && (
                  <p className="py-4 text-sm text-brand-mute" data-testid="draft-library-empty">No drafts match &ldquo;{filter}&rdquo;.</p>
                )}
                {visibleDrafts.map((d) => (
                  <div key={d.id} className={`flex items-center justify-between gap-3 py-2.5 ${d.id === draft?.id ? "bg-brand-red/[0.04]" : ""}`} data-testid={`draft-row-${d.id}`}>
                    <button type="button" onClick={() => openDraft(d.id)} className="min-w-0 flex-1 text-left transition-colors hover:text-brand-red" data-testid={`open-draft-${d.id}`}>
                      <p className="truncate text-sm font-medium text-brand-ink">{d.variants?.[0]?.body?.slice(0, 70) || d.prompt?.slice(0, 70) || d.type}</p>
                      <p className="text-xs capitalize text-brand-mute">
                        {d.type.replace("_", " ")} · {d.origin} · {timeAgo(d.created_at)}
                        {d.scheduled_for ? ` · scheduled ${d.scheduled_for}` : ""}
                      </p>
                    </button>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Copy link to this draft"
                        onClick={() => copyLink(d.id)} data-testid={`copy-draft-link-${d.id}`}>
                        <Link2 className="h-3.5 w-3.5 text-brand-mute" />
                      </Button>
                      {hasRole("admin", "manager", "staff") && (
                        <input
                          type="date"
                          defaultValue={d.scheduled_for || ""}
                          onChange={async (e) => {
                            try {
                              await api.patch(`/content/drafts/${d.id}`, { scheduled_for: e.target.value || "" });
                              qc.invalidateQueries({ queryKey: ["drafts"] });
                              toast.success(e.target.value ? `Scheduled for ${e.target.value}` : "Unscheduled");
                            } catch (err) { toast.error(apiError(err)); }
                          }}
                          className="rounded-md border border-border px-1.5 py-1 text-xs text-brand-mute"
                          data-testid={`schedule-draft-${d.id}`}
                        />
                      )}
                      <Badge variant="outline" className="capitalize">{d.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function VariantCard({ variant, index }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(variant.body);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="animate-fade-up rounded-xl border border-border bg-white p-5 shadow-sm" style={{ animationDelay: `${index * 60}ms` }}>
      <div className="mb-3 flex items-center justify-between">
        <span className="font-heading text-sm font-bold text-brand-red">{variant.label}</span>
        <Button data-testid={`copy-variant-${index}`} variant="outline" size="sm" onClick={copy}>
          {copied ? <Check className="mr-1.5 h-3.5 w-3.5 text-pos-text" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />} Copy
        </Button>
      </div>
      <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed text-brand-ink">{variant.body}</pre>
    </div>
  );
}
